import React from 'react';
import styles from './ui.module.css';
import Skeleton from './Skeleton';

export interface StatItem {
  /** Short uppercase caption, e.g. "Market value". */
  label: string;
  /** The figure, already formatted — use @/utils/formatters. */
  value: React.ReactNode;
  /** Small trailing unit: "Cr", "positions". */
  unit?: string;
  /** Signed change, e.g. "+1.84%". */
  delta?: string;
  deltaTone?: 'up' | 'down' | 'flat';
  /** One line of context under the figure. */
  hint?: React.ReactNode;
  /** Let the hint wrap instead of truncating — for a hint that must be read in
   *  full, like a list of scheme codes. Every tile in the row grows to match. */
  wrapHint?: boolean;
  /** Native tooltip on the whole tile. */
  title?: string;
  /** Makes the tile a button — for tiles that pick a view or a filter. */
  onClick?: () => void;
  /** The tile's view or filter is the one in force. Only meaningful with `onClick`. */
  active?: boolean;
  /** A result the row exists to show (e.g. a computed split), tinted navy-soft.
   *  For a static tile — a clickable one signals selection with `active`. */
  emphasis?: boolean;
  /** Stable identity when two tiles share a label. Defaults to `label`. */
  id?: string;
  /** The figure is still being fetched: keep the tile and its label, shimmer
   *  the value and hint, so the row never disappears and comes back. */
  loading?: boolean;
}

/**
 * Stand-in tiles for a stat row whose data has not arrived. Pass the same
 * labels the loaded row will have, so nothing shifts when the figures land.
 */
export const loadingStats = (labels: string[]): StatItem[] =>
  labels.map(label => ({ label, value: null, loading: true }));

interface StatGridProps {
  items: StatItem[];
  /** Names the group for screen readers when the tiles are buttons. */
  ariaLabel?: string;
  /** Smaller figures and no fixed height — for full-precision amounts or a row
   *  sitting inside a form rather than under a page title. */
  compact?: boolean;
}

const DELTA: Record<'up' | 'down' | 'flat', string> = {
  up: styles.deltaUp,
  down: styles.deltaDown,
  flat: styles.deltaFlat,
};

/**
 * A row of figure tiles. PageHeader renders one under the title; a page can
 * also place one inside a card or above a table.
 *
 * A tile with `onClick` becomes a button (a view picker, a category filter) and
 * shows `active` in navy — the app's selected state — never in a semantic colour.
 */
const StatGrid: React.FC<StatGridProps> = ({ items, ariaLabel, compact }) => {
  if (items.length === 0) return null;
  const interactive = items.some(s => s.onClick);
  const busy = items.some(s => s.loading);
  return (
    <div
      className={`${styles.stats}${compact ? ` ${styles.statsCompact}` : ''}`}
      role={interactive ? 'group' : undefined}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
    >
      {items.map(s => {
        const body = (
          <>
            <span className={styles.statLabel}>{s.label}</span>
            {s.loading ? (
              <>
                <span className={styles.statValue}><Skeleton width={72} height={22} /></span>
                <span className={styles.statFoot}><Skeleton width={120} height={11} /></span>
              </>
            ) : (
              <>
                <span className={styles.statValue}>
                  {s.value}
                  {s.unit && <span className={styles.unit}>{s.unit}</span>}
                </span>
                <span className={styles.statFoot}>
                  {s.delta && <span className={`${styles.delta} ${DELTA[s.deltaTone ?? 'flat']}`}>{s.delta}</span>}
                  {s.hint && <span>{s.hint}</span>}
                </span>
              </>
            )}
          </>
        );
        const key = s.id ?? s.label;
        const tileClass = `${styles.stat}${s.wrapHint ? ` ${styles.statWrap}` : ''}${s.emphasis ? ` ${styles.statEmphasis}` : ''}`;
        if (!s.onClick) {
          return (
            <div key={key} className={tileClass} title={s.title}>
              {body}
            </div>
          );
        }
        return (
          <button
            key={key}
            type="button"
            className={`${tileClass} ${styles.statButton}${s.active ? ` ${styles.statActive}` : ''}`}
            onClick={s.onClick}
            aria-pressed={s.active ?? false}
            title={s.title}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
};

export default StatGrid;
